import { secureApi } from './secureApi';

/**
 * Cleaning Checklist API — checklist, supplies and issue reporting on a
 * cleaning task.
 *
 * Backend routes: be/routes/taskRoutes.js
 * Plan: tasks/cleaning-checklist-supplies.md
 *
 * Separate from the legacy taskApi.js (raw axios + manual headers) — new
 * services use the shared secureApi instance per CLAUDE.md.
 *
 * Every call throws on failure so the caller can surface the server's Arabic
 * message; nothing is swallowed here.
 */
const cleaningChecklistApi = {
  /**
   * Answer one checklist item. Saves on every tap — no submit step.
   * @param {String} taskId
   * @param {Object} payload - { itemId, status: 'done'|'not_done'|'na'|null, comment? }
   * @returns {Promise} { item, progress }
   */
  updateChecklistItem: async (taskId, payload) => {
    const response = await secureApi.patch(`/api/tasks/${taskId}/checklist-item`, payload);
    return response.data.data;
  },

  /**
   * Answer one supplies item.
   * @param {Object} payload - { itemId, status: 'ok'|'needs_restock'|null, note? }
   * @returns {Promise} { item, progress }
   */
  updateSupplyItem: async (taskId, payload) => {
    const response = await secureApi.patch(`/api/tasks/${taskId}/supply-item`, payload);
    return response.data.data;
  },

  /**
   * Report a problem found during a clean — creates a linked maintenance task.
   * Sent as multipart so the optional photo travels in the same request.
   * @param {Object} payload - { category, description, checklistItemId?, photo? (File) }
   */
  reportIssue: async (taskId, payload) => {
    const formData = new FormData();
    formData.append('category', payload.category);
    formData.append('description', payload.description);
    if (payload.checklistItemId) formData.append('checklistItemId', payload.checklistItemId);
    if (payload.photo) formData.append('photo', payload.photo);

    const response = await secureApi.post(`/api/tasks/${taskId}/report-issue`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // A 10MB photo over mobile data needs far more than the client's 10s default
      timeout: 60000
    });
    return response.data.data;
  }
};

export default cleaningChecklistApi;
